import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBaseballBatBall,
  faBasketball,
  faBullseye,
  faCoins,
  faFootball,
  faFutbol,
  faGamepad,
  faGlobe,
  faLocationDot,
  faMedal,
  faMoon,
  faStopwatch,
  faSun,
  faTableTennisPaddleBall,
  faTrophy,
  faUsers,
  faVolleyball,
} from '@fortawesome/free-solid-svg-icons';
import logo from '../assets/logo.png';

/* ═══════════════════ THEME (mirrors Profile.js) ═══════════════════ */
const DARK = {
  mode: 'dark', accent: '#0C69C8', accentGrad: 'linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%)',
  bg: '#0A0A0A', bgAlt: '#111111', bgCard: '#141414', bgElevated: '#1E1E1E',
  border: 'rgba(255,255,255,0.08)', borderMedium: 'rgba(255,255,255,0.14)',
  text: '#FAFAFA', textSecondary: 'rgba(255,255,255,0.68)', textMuted: 'rgba(255,255,255,0.38)',
  shadowMd: '0 4px 16px rgba(0,0,0,0.5)', shadowLg: '0 12px 40px rgba(0,0,0,0.6)',
  gold: '#D4A017', success: '#34D399', danger: '#EF4444', warn: '#F59E0B',
  headerBg: 'rgba(10,10,10,0.92)', accentBgLight: 'rgba(12,105,200,0.14)',
};
const LIGHT = {
  mode: 'light', accent: '#0C69C8', accentGrad: 'linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%)',
  bg: '#F5F7FA', bgAlt: '#EDF0F5', bgCard: '#FFFFFF', bgElevated: '#FFFFFF',
  border: 'rgba(10,29,68,0.08)', borderMedium: 'rgba(10,29,68,0.14)',
  text: '#0F1629', textSecondary: '#3D4A6B', textMuted: '#7A87A8',
  shadowMd: '0 4px 16px rgba(10,29,68,0.09)', shadowLg: '0 12px 40px rgba(10,29,68,0.12)',
  gold: '#B8860B', success: '#059669', danger: '#DC2626', warn: '#D97706',
  headerBg: 'rgba(245,247,250,0.95)', accentBgLight: 'rgba(12,105,200,0.10)',
};

const noImage = 'https://wcislnchclarifyudagt.supabase.co/storage/v1/object/public/images/app-Images/no-image.jpg';

const getSportIcon = s => ({
  Tennis: faTableTennisPaddleBall,
  Basketball: faBasketball,
  Football: faFutbol,
  Padel: faTableTennisPaddleBall,
  Badminton: faTableTennisPaddleBall,
  Volleyball: faVolleyball,
  Rugby: faFootball,
  Cricket: faBaseballBatBall,
}[s] || faMedal);

const STATUS_CONFIG = {
  in_progress: { label: 'LIVE',      color: '#EF4444', bg: 'rgba(239,68,68,0.12)',    border: 'rgba(239,68,68,0.3)',    pulse: true  },
  waiting:     { label: 'STARTING',  color: '#F59E0B', bg: 'rgba(245,158,11,0.10)',   border: 'rgba(245,158,11,0.28)', pulse: false },
  completed:   { label: 'FINISHED',  color: '#34D399', bg: 'rgba(52,211,153,0.10)',   border: 'rgba(52,211,153,0.28)', pulse: false },
};

/* ═══ CSS injected once ═══ */
const injectCSS = () => {
  if (document.getElementById('lm-styles')) return;
  const s = document.createElement('style');
  s.id = 'lm-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=JetBrains+Mono:wght@500;700&display=swap');
    .lm-root * { box-sizing: border-box; margin: 0; padding: 0; }
    .lm-root { font-family: 'Sora', sans-serif; }
    .lm-mono { font-family: 'JetBrains Mono', monospace; }
    .theme-toggle { transition: transform 0.2s ease; }
    .theme-toggle:hover { transform: rotate(15deg) scale(1.1); }
    .grad-text {
      background: linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    @keyframes lm-pulse-ring {
      0%   { transform: scale(1);   opacity: 0.7; }
      70%  { transform: scale(2.2); opacity: 0; }
      100% { transform: scale(2.2); opacity: 0; }
    }
    @keyframes lm-blink {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.25; }
    }
    @keyframes lm-fadein {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes lm-score-pop {
      0%   { transform: scale(1); }
      40%  { transform: scale(1.35); }
      100% { transform: scale(1); }
    }
    @keyframes lm-ticker {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    @keyframes lm-spin {
      to { transform: rotate(360deg); }
    }

    .lm-card {
      transition: transform 0.18s ease, box-shadow 0.18s ease;
    }
    .lm-card:hover {
      transform: translateY(-2px);
    }
    .lm-live-dot {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .lm-live-dot::before {
      content: '';
      position: absolute;
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #EF4444;
      animation: lm-pulse-ring 1.4s ease-out infinite;
    }
    .lm-live-dot-inner {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #EF4444;
      position: relative;
      z-index: 1;
    }
    .lm-blink { animation: lm-blink 1.1s ease-in-out infinite; }
    .lm-fadein { animation: lm-fadein 0.35s ease both; }
    .lm-score-pop { animation: lm-score-pop 0.3s ease; }

    .lm-ticker-wrap {
      overflow: hidden;
      white-space: nowrap;
    }
    .lm-ticker-inner {
      display: inline-flex;
      animation: lm-ticker 28s linear infinite;
    }
    .lm-spinner {
      width: 36px; height: 36px;
      border-radius: 50%;
      border: 3px solid rgba(12,105,200,0.2);
      border-top-color: #0C69C8;
      animation: lm-spin 0.7s linear infinite;
    }
    .lm-filter-btn {
      border: none;
      cursor: pointer;
      font-family: 'Sora', sans-serif;
      font-weight: 700;
      font-size: 12px;
      padding: 6px 14px;
      border-radius: 99px;
      transition: all 0.15s ease;
      white-space: nowrap;
    }
    .lm-avatar {
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }
    .lm-score-box {
      min-width: 52px;
      text-align: center;
      font-family: 'JetBrains Mono', monospace;
      font-weight: 700;
      letter-spacing: -1px;
      border-radius: 10px;
      padding: 10px 8px;
      font-size: 32px;
      line-height: 1;
    }
    .lm-vs {
      font-family: 'JetBrains Mono', monospace;
      font-weight: 700;
      font-size: 11px;
      letter-spacing: 2px;
      opacity: 0.45;
    }
  `;
  document.head.appendChild(s);
};

/* ═══════════════════ LIVE DOT ═══════════════════ */
const LiveDot = () => (
  <span className="lm-live-dot">
    <span className="lm-live-dot-inner" />
  </span>
);

/* ═══════════════════ PLAYER SLOT ═══════════════════ */
const PlayerSlot = ({ C, queue, isWinner, isDark }) => {
  if (!queue) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.bgAlt, border: `1px dashed ${C.border}` }} />
      <span style={{ fontSize: 13, color: C.textMuted }}>Waiting...</span>
    </div>
  );

  const isTeam = queue.mode1 === 'team';
  const members = (() => {
    try { return typeof queue.members === 'string' ? JSON.parse(queue.members) : queue.members; }
    catch { return null; }
  })();
  const img = isTeam ? (members?.[0]?.profile_img || noImage) : (queue.profile_img || noImage);
  const name = isTeam ? (queue.name || 'Team') : `${queue.name || ''}${queue.surname ? ' ' + queue.surname : ''}`.trim() || queue.match_initiator_username || 'Player';

  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, opacity: isWinner === false ? 0.55 : 1, transition: 'opacity 0.3s' }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <img src={img} alt={name} className="lm-avatar" style={{ width: 42, height: 42, border: `2px solid ${isWinner ? C.success : C.border}` }} />
        {isTeam && members?.length > 1 && (
          <img src={members[1]?.profile_img || noImage} alt="" className="lm-avatar"
            style={{ width: 24, height: 24, position: 'absolute', bottom: -4, right: -6, border: `2px solid ${C.bgCard}` }} />
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: isWinner ? C.text : C.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
        {isTeam && members?.length > 1 && (
          <div style={{ fontSize: 10, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {members.map(m => m.username).join(' · ')}
          </div>
        )}
        {!isTeam && queue.match_initiator_username && (
          <div style={{ fontSize: 10, color: C.textMuted }}>@{queue.match_initiator_username}</div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════ MATCH CARD ═══════════════════ */
const getTeamDisplay = (queue) => {
    if (!queue) return { imgs: [], name: null, isIndividual: false, isGroup: false };
  
    const isTeam = queue.mode1 === 'team';
    const members = (() => {
      try { return typeof queue.members === 'string' ? JSON.parse(queue.members) : queue.members; }
      catch { return null; }
    })();
  
    if (!isTeam) return {
      imgs: [queue.profile_img],
      name: `${queue.name || ''}${queue.surname ? ' ' + queue.surname : ''}`.trim() || queue.match_initiator_username || 'Player',
      username: queue.match_initiator_username,
      isIndividual: false,
      isGroup: false,
    };
  
    if (queue.team_type === 'individual') return {
      imgs: (members || []).map(m => m.profile_img).filter(Boolean),
      name: queue.name || 'Team',
      username: null,
      isIndividual: true,
      isGroup: false,
    };
  
    return {
      imgs: [queue.team_image],
      name: queue.name || 'Team',
      username: null,
      isIndividual: false,
      isGroup: true,
    };
  };
  
  const PlayerDisplay = ({ C, queue, isWinner, isDone }) => {
    const { imgs, name, username, isIndividual, isGroup } = getTeamDisplay(queue);
  
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, opacity: isDone && isWinner === false ? 0.5 : 1, transition: 'opacity 0.3s' }}>
        {!queue ? (
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: C.bgAlt, border: `1px dashed ${C.border}` }} />
        ) : isGroup ? (
          // Landscape shape for group teams
          <img
            src={imgs[0] || noImage}
            alt={name}
            style={{ width: 90, height: 44, borderRadius: 10, objectFit: 'cover', border: `2px solid ${isWinner ? C.success : C.border}` }}
          />
        ) : isIndividual ? (
          // Stacked circular avatars for individual teams
          <div style={{ display: 'flex', alignItems: 'center', height: 44 }}>
            {imgs.slice(0, 3).map((img, i) => (
              <img
                key={i}
                src={img || noImage}
                alt=""
                className="lm-avatar"
                style={{ width: 34, height: 34, border: `2px solid ${isWinner ? C.success : C.bgCard}`, marginLeft: i === 0 ? 0 : -10, zIndex: i }}
              />
            ))}
          </div>
        ) : (
          // Single circular avatar for solo
          <img
            src={imgs[0] || noImage}
            alt={name}
            className="lm-avatar"
            style={{ width: 44, height: 44, border: `2px solid ${isWinner ? C.success : C.border}` }}
          />
        )}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, maxWidth: 110, wordBreak: 'break-word', lineHeight: 1.3 }}>
            {name || 'Waiting...'}
          </div>
          {username && <div style={{ fontSize: 10, color: C.textMuted }}>@{username}</div>}
        </div>
      </div>
    );
  };
  
  const MatchCard = ({ C, match, location }) => {
    const { p1, p2, game } = match;
    const sc = STATUS_CONFIG[game.status] || STATUS_CONFIG.waiting;
    const isLive = game.status === 'in_progress';
    const isDone = game.status === 'completed';
  
    const p1Score = p1?.score ?? 0;
    const p2Score = p2?.score ?? 0;
    const p1Wins = isDone && p1Score > p2Score;
    const p2Wins = isDone && p2Score > p1Score;
  
    const duration = (() => {
      if (!game.game_started_at) return null;
      const end = game.ended_at ? new Date(game.ended_at) : new Date();
      const diff = Math.round((end - new Date(game.game_started_at)) / 60000);
      return diff > 0 ? `${diff}m` : null;
    })();
  
    return (
      <div className="lm-card lm-fadein" style={{
        background: C.bgCard,
        border: `1px solid ${sc.border}`,
        borderRadius: 18,
        overflow: 'hidden',
        boxShadow: C.shadowMd,
        position: 'relative',
      }}>
        {/* Top accent bar */}
        <div style={{ height: 3, background: isLive ? 'linear-gradient(90deg, #EF4444, #FF6B6B)' : game.status === 'waiting' ? C.warn : C.success }} />
  
        {/* Location banner */}
        {location?.image_url && (
          <div style={{ position: 'relative', height: 130, overflow: 'hidden' }}>
            <img src={location.image_url} alt={location.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.7))' }} />
            {location.name && (
              <div style={{ position: 'absolute', bottom: 7, left: 10, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.9)', display: 'flex', gap: 4, alignItems: 'center' }}>
                <FontAwesomeIcon icon={faLocationDot} />{location.name}
              </div>
            )}
          </div>
        )}
  
        <div style={{ padding: '14px 16px' }}>
          {/* Header: sport + status */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <FontAwesomeIcon icon={getSportIcon(game.sport_id)} style={{ fontSize: 15, color: C.accent }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{game.sport_id}</span>
              <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: game.mode2 === 'competitive' ? 'rgba(139,92,246,0.12)' : 'rgba(12,105,200,0.10)', border: `1px solid ${game.mode2 === 'competitive' ? 'rgba(139,92,246,0.3)' : 'rgba(12,105,200,0.25)'}`, color: game.mode2 === 'competitive' ? '#8B5CF6' : '#0C69C8' }}>
  {game.mode2 === 'competitive' ? 'Competitive' : 'Casual'}
</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {isLive && <LiveDot />}
              <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 10, fontWeight: 800, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color, letterSpacing: 0.5 }}
                className={isLive ? 'lm-blink' : ''}>
                {sc.label}
              </span>
            </div>
          </div>
  
          {/* Score row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <PlayerDisplay C={C} queue={p1} isWinner={isDone ? p1Wins : undefined} isDone={isDone} />
  
            {/* Score block */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <div className="lm-score-box" style={{ background: p1Wins ? 'rgba(52,211,153,0.10)' : C.bgAlt, border: `1px solid ${p1Wins ? 'rgba(52,211,153,0.25)' : C.border}`, color: p1Wins ? C.success : C.text, fontSize: 36 }}>{p1Score}</div>
              <span className="lm-vs" style={{ color: C.textMuted, fontSize: 10 }}>VS</span>
              <div className="lm-score-box" style={{ background: p2Wins ? 'rgba(52,211,153,0.10)' : C.bgAlt, border: `1px solid ${p2Wins ? 'rgba(52,211,153,0.25)' : C.border}`, color: p2Wins ? C.success : C.text, fontSize: 36 }}>{p2Score}</div>
            </div>
  
            <PlayerDisplay C={C} queue={p2} isWinner={isDone ? p2Wins : undefined} isDone={isDone} />
          </div>
  
          {/* Footer */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, padding: '3px 8px', borderRadius: 6, background: C.bgAlt, border: `1px solid ${C.border}` }}>
              <FontAwesomeIcon icon={faBullseye} style={{ marginRight: 5 }} />First to {game.custom_score}
            </span>
            {duration && (
              <span style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, padding: '3px 8px', borderRadius: 6, background: C.bgAlt, border: `1px solid ${C.border}` }}>
                <FontAwesomeIcon icon={faStopwatch} style={{ marginRight: 5 }} />{duration}
              </span>
            )}
            {game.staked_coins && game.staked_coins !== '0' && (
              <span style={{ fontSize: 10, fontWeight: 700, color: C.gold, padding: '3px 8px', borderRadius: 6, background: 'rgba(212,160,23,0.1)', border: '1px solid rgba(212,160,23,0.25)' }}>
                <FontAwesomeIcon icon={faCoins} style={{ marginRight: 5 }} />{game.staked_coins}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

/* ═══════════════════ TICKER ═══════════════════ */
const Ticker = ({ C, items }) => {
  if (!items.length) return null;
  const text = items.join('   ·   ') + '   ·   ';
  return (
    <div className="lm-ticker-wrap" style={{ background: '#EF4444', padding: '7px 0', borderBottom: `1px solid rgba(255,255,255,0.1)` }}>
      <div className="lm-ticker-inner">
        {[text, text].map((t, i) => (
          <span key={i} className="lm-mono" style={{ fontSize: 11, fontWeight: 700, color: '#fff', paddingRight: 60, letterSpacing: 0.3 }}>{t}</span>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
const LiveMatches = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem('spa-theme') === 'light' ? LIGHT : DARK);
  const C = theme;

  const [liveMatches, setLiveMatches]     = useState([]);
  const [waitingMatches, setWaitingMatches] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [locations, setLocations]         = useState({});
  const [loading, setLoading]             = useState(true);
  const [lastUpdated, setLastUpdated]     = useState(null);
  const [filter, setFilter]               = useState('all'); // 'all' | 'solo' | 'team'
  const [sportFilter, setSportFilter]     = useState('all');
  const [sports, setSports]               = useState([]);
  const channelRef = useRef(null);

  const buildMatches = useCallback(async (games, queueRows) => {
    const queueMap = {};
    for (const q of queueRows) queueMap[q.id] = q;

    const enriched = games.map(g => ({
      game: g,
      p1: queueMap[g.player1_queue_id] || null,
      p2: queueMap[g.player2_queue_id] || null,
    }));

    return enriched;
  }, []);
/*
  const loadData = useCallback(async () => {
    try {
      const [{ data: games }, { data: queues }] = await Promise.all([
        supabase.from('active_matches')
          .select('*')
          .in('status', ['in_progress', 'waiting', 'completed'])
          .order('created_at', { ascending: false })
          .limit(60),
        supabase.from('matchmaking_queue')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(200),
      ]);

      if (!games) return;

      const allMatches = await buildMatches(games, queues || []);

      // Fetch locations
      const locIds = [...new Set(games.map(g => g.location_id).filter(Boolean))];
      if (locIds.length) {
        const { data: locs } = await supabase.from('locations').select('id,name,image_url,address').in('id', locIds);
        const locMap = {};
        for (const l of locs || []) locMap[l.id] = l;
        setLocations(locMap);
      }

      // Collect unique sports
      const sportSet = new Set(games.map(g => g.sport_id).filter(Boolean));
      setSports(['all', ...Array.from(sportSet).sort()]);

      const twoHoursAgo = new Date(Date.now() - 1000 * 60 * 120).toISOString();

      setLiveMatches(allMatches.filter(m =>
        m.game.status === 'in_progress' && m.game.created_at > twoHoursAgo
      ));
      setWaitingMatches(allMatches.filter(m =>
        m.game.status === 'waiting' && m.game.created_at > twoHoursAgo
      ));
      setRecentMatches(allMatches.filter(m =>
        m.game.status === 'completed'
      ).slice(0, 20));
      setLastUpdated(new Date());
    } catch (err) {
      console.error('LiveMatches load error:', err);
    } finally {
      setLoading(false);
    }
  }, [buildMatches]);
  */
  const loadData = useCallback(async () => {
    try {
      // Fetch active + recent matches
      const [{ data: games }, { data: queues }] = await Promise.all([
        supabase.from('active_matches')
          .select('*')
          .in('status', ['in_progress', 'waiting', 'completed'])
          .order('created_at', { ascending: false })
          .limit(60),
        supabase.from('matchmaking_queue')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(200),
      ]);

      if (!games) return;

      const allMatches = await buildMatches(games, queues || []);

      // Fetch locations
      const locIds = [...new Set(games.map(g => g.location_id).filter(Boolean))];
      if (locIds.length) {
        const { data: locs } = await supabase.from('locations').select('id,name,image_url,address').in('id', locIds);
        const locMap = {};
        for (const l of locs || []) locMap[l.id] = l;
        setLocations(locMap);
      }

      // Collect unique sports
      const sportSet = new Set(games.map(g => g.sport_id).filter(Boolean));
      setSports(['all', ...Array.from(sportSet).sort()]);

      setLiveMatches(allMatches.filter(m => m.game.status === 'in_progress'));
      setWaitingMatches(allMatches.filter(m => m.game.status === 'waiting'));
      setRecentMatches(allMatches.filter(m => m.game.status === 'completed').slice(0, 20));
      setLastUpdated(new Date());
    } catch (err) {
      console.error('LiveMatches load error:', err);
    } finally {
      setLoading(false);
    }
  }, [buildMatches]);

  useEffect(() => {
    injectCSS();
    loadData();

    // Subscribe to real-time changes on active_matches and matchmaking_queue
    const channel = supabase.channel('live-matches-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'active_matches' }, () => loadData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'matchmaking_queue' }, () => loadData())
      .subscribe();

    channelRef.current = channel;

    // Fallback poll every 15s
    const poll = setInterval(loadData, 15000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [loadData]);

  const applyFilters = (list) => list.filter(m => {
    if (filter !== 'all' && m.game.mode1 !== filter) return false;
    if (sportFilter !== 'all' && m.game.sport_id !== sportFilter) return false;
    return true;
  });

  const filteredLive    = applyFilters(liveMatches);
  const filteredWaiting = applyFilters(waitingMatches);
  const filteredRecent  = applyFilters(recentMatches);

  // Ticker messages from live matches
  const tickerItems = liveMatches.map(m => {
    const p1 = m.p1?.name || m.p1?.match_initiator_username || 'P1';
    const p2 = m.p2?.name || m.p2?.match_initiator_username || 'P2';
    return `${m.game.sport_id || 'Sport'} ${p1} ${m.p1?.score ?? 0}-${m.p2?.score ?? 0} ${p2}`;
  });

  const toggleTheme = () => {
    const next = C.mode === 'dark' ? LIGHT : DARK;
    setTheme(next);
    localStorage.setItem('spa-theme', next.mode);
  };

  return (
    <div className="lm-root" style={{ minHeight: '100vh', background: C.bg, color: C.text }}>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 100, height: 56, padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.headerBg, backdropFilter: 'blur(20px) saturate(1.2)', WebkitBackdropFilter: 'blur(20px) saturate(1.2)', borderBottom: `1px solid ${C.border}` }}>
        <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: 'contain' }} />
          </div>
          <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 700, color: C.text }}>
            Live <span style={{ color: C.accent }}>Matches</span>
          </span>
        </button>
        <button onClick={loadData} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', fontSize: 10, fontWeight: 800, letterSpacing: 0.4, cursor: 'pointer' }}>
          <LiveDot /> {liveMatches.length > 0 ? `${liveMatches.length} LIVE` : 'LIVE'}
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="theme-toggle" aria-label={C.mode === "dark" ? "Use light theme" : "Use dark theme"} onClick={toggleTheme} style={{ width: 32, height: 32, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FontAwesomeIcon icon={C.mode === 'dark' ? faSun : faMoon} style={{ fontSize: 12 }} />
          </button>
          <button onClick={() => navigate(-1)} style={{ fontSize: 12, fontWeight: 500, color: C.textSecondary, padding: '5px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgElevated, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
            ← Back
          </button>
        </div>
      </header>

      {/* Live ticker */}
      {tickerItems.length > 0 && <Ticker C={C} items={tickerItems} />}

      {/* Main content */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <div className="lm-spinner" />
          </div>
        ) : (
          <>
            {/* Filters */}
            <div style={{ marginBottom: 24 }}>
              {/* Mode toggle: All / Solo / Team — centred */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 3, background: C.bgAlt, border: `1px solid ${C.border}`, padding: 3, borderRadius: 99 }}>
                  {[{ v: 'all', l: 'All', icon: faGlobe }, { v: 'solo', l: 'Solo', icon: faGamepad }, { v: 'team', l: 'Team', icon: faUsers }].map(opt => (
                    <button key={opt.v} className="lm-filter-btn" onClick={() => setFilter(opt.v)}
                      style={{ background: filter === opt.v ? C.accentGrad : 'transparent', color: filter === opt.v ? '#fff' : C.textSecondary }}>
                      <FontAwesomeIcon icon={opt.icon} style={{ marginRight: 6 }} />{opt.l}
                    </button>
                  ))}
                </div>
              </div>
              {/* Sport pills — centred, wrap */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                {sports.map(s => (
                  <button key={s} className="lm-filter-btn" onClick={() => setSportFilter(s)} style={{
                    background: sportFilter === s ? C.accentGrad : C.bgAlt,
                    color: sportFilter === s ? '#fff' : C.textSecondary,
                    border: `1px solid ${sportFilter === s ? 'transparent' : C.border}`,
                  }}>
                    {s === 'all' ? <><FontAwesomeIcon icon={faMedal} style={{ marginRight: 6 }} />All Sports</> : <><FontAwesomeIcon icon={getSportIcon(s)} style={{ marginRight: 6 }} />{s}</>}
                  </button>
                ))}
              </div>
            </div>

            {/* ── LIVE ── */}
            {filteredLive.length > 0 && (
              <section style={{ marginBottom: 36 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <LiveDot />
                  <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>In Progress</h2>
                  <span style={{ fontSize: 12, color: C.textMuted }}>{filteredLive.length} match{filteredLive.length !== 1 ? 'es' : ''}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(280px,100%),1fr))', gap: 14 }}>
                  {filteredLive.map(m => <MatchCard key={m.game.id} C={C} match={m} location={locations[m.game.location_id]} />)}
                </div>
              </section>
            )}

            {/* ── WAITING ── */}
            {filteredWaiting.length > 0 && (
              <section style={{ marginBottom: 36 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.warn }} className="lm-blink" />
                  <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>Starting Soon</h2>
                  <span style={{ fontSize: 12, color: C.textMuted }}>{filteredWaiting.length} match{filteredWaiting.length !== 1 ? 'es' : ''}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(280px,100%),1fr))', gap: 14 }}>
                  {filteredWaiting.map(m => <MatchCard key={m.game.id} C={C} match={m} location={locations[m.game.location_id]} />)}
                </div>
              </section>
            )}

            {/* ── RECENT ── */}
            {filteredRecent.length > 0 && (
              <section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.success }} />
                  <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>Recently Finished</h2>
                  <span style={{ fontSize: 12, color: C.textMuted }}>{filteredRecent.length} match{filteredRecent.length !== 1 ? 'es' : ''}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(280px,100%),1fr))', gap: 14 }}>
                  {filteredRecent.map(m => <MatchCard key={m.game.id} C={C} match={m} location={locations[m.game.location_id]} />)}
                </div>
              </section>
            )}

            {/* Empty state */}
            {filteredLive.length === 0 && filteredWaiting.length === 0 && filteredRecent.length === 0 && (
              <div style={{ textAlign: 'center', padding: '80px 32px' }}>
                <FontAwesomeIcon icon={faTrophy} style={{ fontSize: 46, marginBottom: 16, color: C.textMuted }} />
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>No matches right now</div>
                <div style={{ fontSize: 14, color: C.textMuted }}>Check back soon — matches appear here in real time.</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LiveMatches;
